import { DOCUMENT, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CanActivateFn } from '@angular/router';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';

const defaultIconUrl = 'assets/mainicon.ico';
const emptyIconUrl = 'data:,';

@Injectable({
  providedIn: 'root'
})
export class FaviconService {
  private document = inject(DOCUMENT);
  private auth = inject(AuthFacade);

  private currentUser = toSignal(this.auth.currentUser$);
  private isUserLoading = toSignal(this.auth.loading$);

  constructor() {
    effect(() => {
      const user = this.currentUser();

      if (user?.Id) {
        const branding = user.BrandingSetting;

        this.setIcon(branding?.BrandingActivated ? branding.TabIconUrl : null);
      }
    });

    effect(() => {
      if (this.isUserLoading() && !this.currentUser()) {
        this.clear();
      }
    });
  }

  public setIcon(iconUrl: string): void {
    if (!iconUrl) {
      return this.setHref(defaultIconUrl);
    }

    this.setHref(this.isAbsoluteUrl(iconUrl) ? iconUrl : `${location.origin}/${iconUrl}`);
  }

  public setDefaultIfEmpty(): void {
    if (this.faviconElement.getAttribute('href') === emptyIconUrl) {
      this.setHref(defaultIconUrl);
    }
  }

  public clear(): void {
    this.setHref(emptyIconUrl);
  }

  private get faviconElement(): HTMLLinkElement {
    return this.document.getElementById('favicon') as HTMLLinkElement;
  }

  private setHref(href: string): void {
    if (this.faviconElement.getAttribute('href') !== href) {
      this.faviconElement.setAttribute('href', href);
    }
  }

  private isAbsoluteUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }
}

export const defaultFaviconGuard: CanActivateFn = () => {
  inject(FaviconService).setDefaultIfEmpty();

  return true;
};
